import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddressPicker from '../../common/AddressPicker';

/**
 * Section 2 of the 5-section campaign registration layout.
 *
 * Scope: the short "제공 제품/서비스 정보" card from the reference layout —
 * product/service name, market value, and visit address with embedded
 * map. The longer-form介绍 fields (intro, provision method, visit notes,
 * attachments, photos) live in {@link ../sections/CampaignDetailV2} to
 * match the reference grouping.
 *
 * Field names (data model) are unchanged: `productName`,
 * `providedValueDescription`, `visitAddress`, `visitLatitude`,
 * `visitLongitude`.
 */
interface Props {
  control: Control<any>;
  setValue: any;
  watch: any;
}

const ProductServiceBasicV2: React.FC<Props> = ({ control, setValue, watch }) => {
  const { t } = useTranslation(['campaigns', 'common']);

  return (
    <div className="space-y-4">
      {/* 제공 제품/서비스명 */}
      <Controller
        name="productName"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.productName')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...field}
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                fieldState.error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
          </div>
        )}
      />

      {/* 제품/서비스 가치 */}
      <Controller
        name="providedValueDescription"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.productValue')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...field}
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                fieldState.error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
          </div>
        )}
      />

      {/* 방문 주소 */}
      <Controller
        name="visitAddress"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.visitAddress')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <AddressPicker
              mode="single"
              value={{
                text: field.value || '',
                latitude: watch('visitLatitude') ?? null,
                longitude: watch('visitLongitude') ?? null,
              }}
              onChange={(next) => {
                field.onChange(next.text);
                setValue('visitLatitude', next.latitude);
                setValue('visitLongitude', next.longitude);
              }}
              hasError={!!fieldState.error}
            />
          </div>
        )}
      />
    </div>
  );
};

export default ProductServiceBasicV2;
